import { Pill, PILL_COLORS } from "components/ui/Pill";
import {
  getLabTestLabel,
  getLabTestStatusLabel,
} from "features/labOrders/utils/outstandingLabTests";
import { formatDate } from "utils/dateFormatter";

import s from "./OutstandingLabTestsTable.module.css";

const Muted = ({ children }) => <span className={s.muted}>{children}</span>;

// The test's own code under its name: a GP recognises "TSH" faster than the lab's full
// wording, and it is the code the lab will be quoted on the phone.
const TestCell = ({ test }) => (
  <span className={s.stacked}>
    <span className={s.primaryText}>{getLabTestLabel(test)}</span>
    {!!test.testCode && test.testCode !== test.testName && (
      <span className={s.secondaryText}>{test.testCode}</span>
    )}
  </span>
);

// Date and ordering GP in one column, because "ordered" is one fact with two halves and
// splitting them costs a column the row cannot spare.
const OrderedCell = ({ test }) => (
  <span className={s.stacked}>
    <span className={s.primaryText}>
      {formatDate(test.requestedAt) || <Muted>Date unknown</Muted>}
    </span>
    <span className={s.secondaryText}>
      {test.orderingStaffName || "Ordering GP unknown"}
    </span>
  </span>
);

/*
 * Status and Overdue are two different statements and both are shown: the status says
 * whether anything has come back at all, while Overdue says the lab is late. A
 * preliminary result that is late is both, and collapsing them into one pill would hide
 * whichever the practice happened not to pick.
 */
const StatusCell = ({ test }) => (
  <span className={s.statusCell}>
    <Pill {...PILL_COLORS.statusPending}>
      {getLabTestStatusLabel(test.status)}
    </Pill>
    {!!test.overdue && (
      <Pill {...PILL_COLORS.statusError}>Overdue</Pill>
    )}
  </span>
);

export const getOutstandingLabTestsColumns = () => [
  {
    id: "patientName",
    header: "Patient",
    width: "minmax(120px, 180px)",
    render: (test) => test.patientName || <Muted>-</Muted>,
  },
  {
    id: "testName",
    header: "Test",
    width: "minmax(140px, 260px)",
    render: (test) => <TestCell test={test} />,
  },
  {
    id: "section",
    header: "Section",
    width: "minmax(80px, 110px)",
    render: (test) => test.section || <Muted>-</Muted>,
  },
  {
    id: "hospitalName",
    header: "Hospital",
    width: "minmax(130px, 220px)",
    render: (test) => test.hospitalName || <Muted>-</Muted>,
  },
  {
    id: "requestedAt",
    header: "Ordered",
    width: "minmax(120px, 170px)",
    render: (test) => <OrderedCell test={test} />,
  },
  {
    id: "status",
    header: "Status",
    // `wrap` so a row carrying both pills is not clipped by the row's line box.
    width: "minmax(150px, 1fr)",
    wrap: true,
    render: (test) => <StatusCell test={test} />,
  },
];
